import type { Item, ViewOptionFields, ViewType } from "../api/types";
import { compareOrder, keyBetween } from "../lib/tasks";
import { useApp } from "../store";
import { command, newUuid, type SyncEngine } from "./engine";

/** All writes go through here as Sync commands (optimistic, with temp ids). */

function eng(): SyncEngine {
  const e = useApp.getState().engine;
  if (!e) throw new Error("not logged in");
  return e;
}

const tempId = () => `tmp-${newUuid()}`;

export interface NewTask {
  content: string;
  description?: string;
  project_id?: string;
  section_id?: string | null;
  parent_id?: string | null;
  priority?: number;
  due?: { string: string } | { date: string } | null;
  labels?: string[];
  order_key?: string;
  responsible_uid?: string | null;
}

export function addTask(task: NewTask) {
  const id = tempId();
  const args: Record<string, unknown> = { ...task };
  for (const k of Object.keys(args)) if (args[k] === undefined) delete args[k];
  void eng().enqueue(command("item_add", args, id));
  return id;
}

export function updateTask(id: string, fields: Record<string, unknown>) {
  return eng().enqueue(command("item_update", { id, ...fields }));
}

export function completeTask(item: Item, onUndo?: () => void) {
  void eng().enqueue(command("item_close", { id: item.id }));
  const t = useApp.getState();
  t.toast({
    message: "taskCompleted",
    action: {
      label: "undo",
      run: () => {
        if (item.due?.is_recurring) void eng().enqueue(command("item_update", { id: item.id, due: item.due }));
        else void eng().enqueue(command("item_uncomplete", { id: item.id }));
        onUndo?.();
      },
    },
  });
}

export function uncompleteTask(id: string) {
  return eng().enqueue(command("item_uncomplete", { id }));
}

export function deleteTask(id: string) {
  return eng().enqueue(command("item_delete", { id }));
}

/**
 * Move a task to a new position. `target` is where it lands (project/section/parent) and
 * `prev`/`next` its new neighbors there. Container changes go through item_move, the
 * position through item_update order_key (the non-deprecated reorder).
 */
export function moveTask(
  item: Item,
  target: { project_id: string; section_id: string | null; parent_id: string | null },
  prev: Item | undefined,
  next: Item | undefined,
) {
  const cmds = [];
  if (target.parent_id && target.parent_id !== item.parent_id) {
    cmds.push(command("item_move", { id: item.id, parent_id: target.parent_id }));
  } else if (!target.parent_id && (item.parent_id || target.section_id !== item.section_id)) {
    cmds.push(
      target.section_id
        ? command("item_move", { id: item.id, section_id: target.section_id })
        : command("item_move", { id: item.id, project_id: target.project_id }),
    );
  } else if (target.project_id !== item.project_id) {
    cmds.push(command("item_move", { id: item.id, project_id: target.project_id }));
  }
  cmds.push(command("item_update", { id: item.id, order_key: keyBetween(prev, next) }));
  return eng().enqueue(cmds);
}

export function moveTaskToProject(id: string, projectId: string, sectionId: string | null = null) {
  return eng().enqueue(
    sectionId
      ? command("item_move", { id, section_id: sectionId })
      : command("item_move", { id, project_id: projectId }),
  );
}

export function addProject(args: {
  name: string;
  color?: string;
  parent_id?: string | null;
  is_favorite?: boolean;
  view_style?: string;
  order_key?: string;
  workspace_id?: string;
}) {
  const id = tempId();
  const a: Record<string, unknown> = { ...args };
  for (const k of Object.keys(a)) if (a[k] === undefined || a[k] === null) delete a[k];
  void eng().enqueue(command("project_add", a, id));
  return id;
}

export const updateProject = (id: string, fields: Record<string, unknown>) =>
  eng().enqueue(command("project_update", { id, ...fields }));
export const moveProject = (id: string, parentId: string | null) =>
  eng().enqueue(command("project_move", { id, parent_id: parentId }));
export const deleteProject = (id: string) => eng().enqueue(command("project_delete", { id }));
export const archiveProject = (id: string) => eng().enqueue(command("project_archive", { id }));

export function addSection(projectId: string, name: string, orderKey?: string) {
  const id = tempId();
  void eng().enqueue(
    command("section_add", { project_id: projectId, name, ...(orderKey ? { order_key: orderKey } : {}) }, id),
  );
  return id;
}
export const updateSection = (id: string, fields: Record<string, unknown>) =>
  eng().enqueue(command("section_update", { id, ...fields }));
export const deleteSection = (id: string) => eng().enqueue(command("section_delete", { id }));

export function addComment(itemId: string, content: string, fileAttachment?: Record<string, unknown>) {
  const id = tempId();
  return eng().enqueue(
    command(
      "note_add",
      { item_id: itemId, content, ...(fileAttachment ? { file_attachment: fileAttachment } : {}) },
      id,
    ),
  );
}
export const deleteComment = (id: string) => eng().enqueue(command("note_delete", { id }));
export const updateComment = (id: string, content: string) => eng().enqueue(command("note_update", { id, content }));

export function addLabel(name: string, color?: string, isFavorite?: boolean) {
  const id = tempId();
  void eng().enqueue(command("label_add", { name, ...(color ? { color } : {}), is_favorite: Boolean(isFavorite) }, id));
  return id;
}
export const updateLabel = (id: string, fields: Record<string, unknown>) =>
  eng().enqueue(command("label_update", { id, ...fields }));
export const deleteLabel = (id: string) => eng().enqueue(command("label_delete", { id }));

export function addFilter(name: string, query: string, color?: string, isFavorite?: boolean) {
  const id = tempId();
  void eng().enqueue(
    command("filter_add", { name, query, ...(color ? { color } : {}), is_favorite: Boolean(isFavorite) }, id),
  );
  return id;
}
export const updateFilter = (id: string, fields: Record<string, unknown>) =>
  eng().enqueue(command("filter_update", { id, ...fields }));
export const deleteFilter = (id: string) => eng().enqueue(command("filter_delete", { id }));

export function addReminder(
  itemId: string,
  reminder: { type: "relative"; minute_offset: number } | { type: "absolute"; due: { string: string } },
) {
  return eng().enqueue(command("reminder_add", { item_id: itemId, ...reminder }, tempId()));
}
export const deleteReminder = (id: string) => eng().enqueue(command("reminder_delete", { id }));

export function setViewOptions(viewType: ViewType, objectId: string | null, fields: ViewOptionFields) {
  return eng().enqueue(
    command("view_options_set", { view_type: viewType, ...(objectId ? { object_id: objectId } : {}), ...fields }),
  );
}

export const updateUser = (fields: Record<string, unknown>) => eng().enqueue(command("user_update", fields));

/** Reorder by setting order keys for a dragged sibling list (section reorder). */
export function reorderSection(
  sections: { id: string; order_key?: string | null; section_order: number }[],
  from: number,
  to: number,
) {
  const list = [...sections].sort(compareOrder);
  const [moved] = list.splice(from, 1);
  if (!moved) return;
  list.splice(to, 0, moved);
  return eng().enqueue(
    command("section_reorder", { sections: list.map((s, i) => ({ id: s.id, section_order: i + 1 })) }),
  );
}
